'use client';

import React from 'react';
import { Search, LayoutGrid, List, Plus, X } from 'lucide-react';
import { EventStatus } from './mockEvents';

interface Props {
  statusFilter: EventStatus | 'all';
  onStatusFilter: (s: EventStatus | 'all') => void;
  viewMode: 'grid' | 'list';
  onViewMode: (v: 'grid' | 'list') => void;
  sortBy: 'date' | 'priority' | 'contacts';
  onSortBy: (s: 'date' | 'priority' | 'contacts') => void;
  search: string;
  onSearch: (v: string) => void;
  counts: Record<string, number>;
  total: number;
}

const statusTabs: { key: EventStatus | 'all'; label: string }[] = [
  { key: 'all', label: 'All Events' },
  { key: 'active', label: 'Active' },
  { key: 'planned', label: 'Planned' },
  { key: 'completed', label: 'Completed' },
  { key: 'archived', label: 'Archived' },
];

export default function EventsListHeader({
  statusFilter, onStatusFilter, viewMode, onViewMode,
  sortBy, onSortBy, search, onSearch, counts, total,
}: Props) {
  return (
    <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border px-6 xl:px-8 2xl:px-10 py-4 max-w-screen-2xl mx-auto">
      <div className="flex items-center justify-between gap-4 mb-4">
        <div>
          <h1 className="text-xl font-semibold text-foreground tracking-tight">Events</h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            <span className="font-mono font-semibold text-foreground">{total}</span> events
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Sort */}
          <div className="relative">
            <select
              value={sortBy}
              onChange={(e) => onSortBy(e.target.value as 'date' | 'priority' | 'contacts')}
              className="input-base h-9 pr-8 text-xs appearance-none cursor-pointer"
              style={{ paddingRight: '2rem' }}
            >
              <option value="date">Sort by Date</option>
              <option value="priority">Sort by Priority</option>
              <option value="contacts">Sort by Contacts</option>
            </select>
          </div>
          {/* View toggle */}
          <div className="flex items-center border border-border rounded-lg overflow-hidden">
            <button
              onClick={() => onViewMode('grid')}
              className={`p-2 transition-colors ${viewMode === 'grid' ? 'bg-primary text-white' : 'bg-white text-muted-foreground hover:bg-muted'}`}
              aria-label="Grid view"
            >
              <LayoutGrid size={14} />
            </button>
            <button
              onClick={() => onViewMode('list')}
              className={`p-2 transition-colors ${viewMode === 'list' ? 'bg-primary text-white' : 'bg-white text-muted-foreground hover:bg-muted'}`}
              aria-label="List view"
            >
              <List size={14} />
            </button>
          </div>
          <button className="btn-primary text-xs">
            <Plus size={14} />
            Create Event
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3">
        {/* Status tabs */}
        <div className="flex items-center gap-1 border border-border rounded-lg p-0.5 bg-white">
          {statusTabs.map((tab) => (
            <button
              key={`stab-${tab.key}`}
              onClick={() => onStatusFilter(tab.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-150 ${
                statusFilter === tab.key
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              {tab.label}
              <span className={`text-[10px] font-mono px-1 py-0.5 rounded ${statusFilter === tab.key ? 'bg-white/20 text-white' : 'bg-muted text-muted-foreground'}`}>
                {counts[tab.key] ?? 0}
              </span>
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative ml-auto w-64">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search events…"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            className="input-base pl-8 pr-7 text-xs h-9"
          />
          {search && (
            <button
              onClick={() => onSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X size={12} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}